import React, { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { MessageCircleReply, Pencil, Trash2, Save, X, Plus } from 'lucide-react';
import { apiGet, apiPost, apiPut, apiDel } from '../../../lib/api.js';
import MergeTagChips from '../../../components/marketing/MergeTagChips.jsx';

const MAX_BODY = 1700;
const KEY = ['preset-replies'];
const errText = (e, fb) => e?.response?.data?.error || e?.message || fb;

/** Problem with a draft, or '' when it can be saved. */
export function replyProblem({ name, body }) {
  if (!String(name || '').trim()) return 'Give the reply a title';
  if (!String(body || '').trim()) return 'Write the message';
  if (String(body).length > MAX_BODY) return `Message is too long (max ${MAX_BODY} characters)`;
  return '';
}

/**
 * Marketing Hub → Preset Replies: saved texts that show up in the Phone Panel's Send Text tab
 * ("Send a preset reply"). Real data only; saving reports exactly what went wrong.
 * @returns {JSX.Element}
 */
export default function PresetRepliesPanel() {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({ queryKey: KEY, queryFn: () => apiGet('/marketing/preset-replies') });
  const replies = data?.presetReplies || [];
  const [draft, setDraft] = useState({ name: '', body: '' });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const bodyRef = useRef(null);

  const reset = () => { setDraft({ name: '', body: '' }); setEditId(null); setError(''); };

  async function save() {
    const problem = replyProblem(draft);
    if (problem) { setError(problem); return; }
    setError('');
    setSaving(true);
    try {
      const payload = { name: draft.name.trim(), body: draft.body.trim() };
      const r = editId ? await apiPut(`/marketing/preset-replies/${editId}`, payload) : await apiPost('/marketing/preset-replies', payload);
      const saved = r.presetReply;
      // Show it immediately, then refresh from the server (and the Phone Panel, which shares this list).
      qc.setQueryData(KEY, (old) => {
        const list = old?.presetReplies || [];
        return { ...(old || { ok: true }), presetReplies: editId ? list.map((x) => (x._id === saved._id ? saved : x)) : [saved, ...list] };
      });
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['marketing-summary'] });
      toast.success(editId ? 'Preset reply updated' : 'Preset reply saved — it now appears in the Phone Panel → Send Text');
      reset();
    } catch (e) {
      const msg = errText(e, 'Could not save the reply');
      setError(msg);
      toast.error(msg);
    }
    setSaving(false);
  }

  async function remove(r) {
    if (!window.confirm(`Delete “${r.name}”?`)) return;
    try {
      await apiDel(`/marketing/preset-replies/${r._id}`);
      qc.setQueryData(KEY, (old) => ({ ...(old || { ok: true }), presetReplies: (old?.presetReplies || []).filter((x) => x._id !== r._id) }));
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['marketing-summary'] });
      if (editId === r._id) reset();
      toast.success('Deleted');
    } catch (e) {
      toast.error(errText(e, 'Could not delete the reply'));
    }
  }

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="preset-replies-panel">
      <div className="panel p-5 space-y-3">
        <div className="font-display font-bold text-lg flex items-center gap-2">
          <MessageCircleReply className="w-4 h-4 text-civix-purple" /> Saved Replies
        </div>
        <p className="text-xs text-white/50">These appear in the Phone Panel → Send Text tab under “Send a preset reply”.</p>
        {isLoading && <div className="text-white/50 text-sm">Loading…</div>}
        {isError && <div className="text-rose-300 text-sm">Could not load your replies. Please refresh.</div>}
        {!isLoading && !isError && replies.length === 0 && (
          <div className="rounded-lg border border-dashed border-white/15 p-6 text-center text-sm text-white/50" data-testid="preset-replies-empty">
            No preset replies yet. Create your first one on the right.
          </div>
        )}
        <ul className="space-y-2">
          {replies.map((r) => (
            <li key={r._id} className={`border rounded-lg p-3 transition ${editId === r._id ? 'border-civix-purple/60' : 'border-civix-border/40 hover:border-civix-purple/40'}`} data-testid={`preset-reply-${r._id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium text-sm text-civix-cyan truncate">{r.name}</div>
                <div className="flex gap-1">
                  <button type="button" aria-label={`Edit ${r.name}`} onClick={() => { setEditId(r._id); setDraft({ name: r.name, body: r.body }); setError(''); }} className="text-white/40 hover:text-white p-1"><Pencil className="w-3.5 h-3.5" /></button>
                  <button type="button" aria-label={`Delete ${r.name}`} onClick={() => remove(r)} className="text-white/40 hover:text-rose-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <div className="text-xs text-white/55 mt-1 line-clamp-2 whitespace-pre-line">{r.body}</div>
            </li>
          ))}
        </ul>
      </div>

      <div className="panel p-5 space-y-3 h-fit">
        <div className="font-display font-bold flex items-center justify-between">
          <span className="flex items-center gap-2">{editId ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}{editId ? 'Edit Preset Reply' : 'New Preset Reply'}</span>
          {editId && <button type="button" onClick={reset} aria-label="Cancel editing" className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>}
        </div>
        <input
          className="input text-sm w-full" placeholder="Title (shown in the dropdown)" value={draft.name} maxLength={128}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="preset-reply-title"
        />
        <textarea
          ref={bodyRef} className="input min-h-36 resize-y text-sm w-full" placeholder="Message" value={draft.body} maxLength={MAX_BODY}
          onChange={(e) => setDraft({ ...draft, body: e.target.value })} data-testid="preset-reply-body"
        />
        <MergeTagChips value={draft.body} onChange={(v) => setDraft({ ...draft, body: v })} textareaRef={bodyRef} max={MAX_BODY} testId="preset-reply-chips" />
        <div className="text-xs text-white/40">{draft.body.length}/{MAX_BODY} characters · est. cost: ${(Math.max(draft.body.length, 1) / 160 * 0.0079).toFixed(4)}</div>
        {error && <div className="text-xs text-rose-300" role="alert" data-testid="preset-reply-error">{error}</div>}
        <button type="button" className="btn-primary w-full text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50" onClick={save} disabled={saving} data-testid="preset-reply-save">
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : editId ? 'Update reply' : 'Save preset reply'}
        </button>
      </div>
    </div>
  );
}
